import { cx } from './cx.ts';

/**
 * The ink dot — Field Notes' status mark. A small round point of ink
 * on paper: no glow, no shadow (paper surfaces carry hierarchy through
 * hairlines, DESIGN.md §4). `pulse` breathes and is reserved for LIVE
 * states (streaming, heartbeat) — at most one pulsing dot per view
 * region.
 */
export type LampTone = 'ok' | 'warn' | 'bad' | 'rust' | 'off';
export type LampSize = 'sm' | 'md';

const TONE_CLASSES: Record<LampTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  rust: 'bg-rust',
  off: 'bg-line',
};

const SIZE_CLASSES: Record<LampSize, string> = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
};

interface Props {
  readonly tone: LampTone;
  readonly size?: LampSize;
  readonly pulse?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function StatusLamp({
  tone,
  size = 'md',
  pulse = false,
  className,
  testId,
}: Props): JSX.Element {
  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      data-tone={tone}
      className={cx(
        'inline-block shrink-0 rounded-full',
        TONE_CLASSES[tone],
        SIZE_CLASSES[size],
        pulse && tone !== 'off' && 'animate-lamp-pulse',
        className,
      )}
    />
  );
}
